import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, History } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { KPICard, CustomTooltip, StatementHeader, SectionRow } from './ReportHelpers';

export function ProfitLossReport({ reportData, viewMode, entityName, includePY }) {
  if (!reportData) return null;
  const revAccounts = reportData.revenue?.accounts || [];
  const expAccounts = reportData.expenses?.accounts || [];
  const revTotal = reportData.revenue?.total || 0;
  const expTotal = reportData.expenses?.total || 0;
  const net = reportData.net_income || 0;
  const py = reportData.prior_year;
  const hasPY = includePY && !!py;

  if (viewMode === 'list') {
    return (
      <Card data-testid="pl-list-view">
        <CardContent className="py-8 px-6 max-w-3xl mx-auto">
          <StatementHeader entityName={entityName} title="Profit & Loss Statement" period={reportData.period} />
          {hasPY && <p className="text-xs text-zinc-400 text-center mb-4">Prior Year: FY {py.fiscal_year}</p>}
          <table className="w-full text-sm">
            {hasPY && (
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="py-2 px-4 text-left text-xs text-zinc-500"></th>
                  <th className="py-2 px-4 text-right text-xs text-zinc-500 font-semibold">Current</th>
                  <th className="py-2 px-4 text-right text-xs text-zinc-400 font-semibold pr-6">PY ({py.fiscal_year})</th>
                </tr>
              </thead>
            )}
            <tbody>
              <SectionRow label="REVENUE" bold />
              {revAccounts.map(a => <SectionRow key={a.code} label={`${a.code}  ${a.name}`} amount={a.balance} pyAmount={hasPY ? a.py_balance : undefined} indent />)}
              <SectionRow label="Total Revenue" amount={revTotal} pyAmount={hasPY ? py.revenue_total : undefined} total />
              <tr><td className="py-3" colSpan={hasPY ? "3" : "2"}></td></tr>
              <SectionRow label="EXPENSES" bold />
              {expAccounts.map(a => <SectionRow key={a.code} label={`${a.code}  ${a.name}`} amount={-a.balance} pyAmount={hasPY ? -a.py_balance : undefined} indent negative />)}
              <SectionRow label="Total Expenses" amount={-expTotal} pyAmount={hasPY ? -py.expenses_total : undefined} total negative />
              <tr><td className="py-3" colSpan={hasPY ? "3" : "2"}></td></tr>
              <SectionRow label="NET INCOME" amount={net} pyAmount={hasPY ? py.net_income : undefined} grandTotal />
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  }

  const chartData = [
    ...revAccounts.map(a => ({ name: a.name, Revenue: a.balance, Expense: 0 })),
    ...expAccounts.map(a => ({ name: a.name, Revenue: 0, Expense: a.balance })),
  ];

  return (
    <div className="space-y-6">
      <div className={`grid grid-cols-1 ${hasPY ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
        <KPICard title="Total Revenue" value={formatCurrency(revTotal)} subtitle={hasPY ? `PY: ${formatCurrency(py.revenue_total)}` : undefined} icon={TrendingUp} color="green" />
        <KPICard title="Total Expenses" value={formatCurrency(expTotal)} subtitle={hasPY ? `PY: ${formatCurrency(py.expenses_total)}` : undefined} icon={TrendingDown} color="red" />
        <KPICard title="Net Income" value={formatCurrency(net)} subtitle={hasPY ? `PY: ${formatCurrency(py.net_income)}` : undefined} icon={DollarSign} color={net >= 0 ? 'green' : 'red'} />
        {hasPY && <KPICard title={`PY Net (${py.fiscal_year})`} value={formatCurrency(py.net_income)} icon={History} color="zinc" />}
      </div>

      {chartData.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Revenue vs Expenses</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 120, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Revenue" fill="#34D399" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="Expense" fill="#F87171" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-sm uppercase tracking-wider text-zinc-500">Revenue</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="report-table" data-testid="pl-revenue-table">
              {hasPY && <thead><tr><th className="pl-4">Code</th><th>Account</th><th className="text-right">Current</th><th className="text-right pr-4 text-zinc-400">PY</th></tr></thead>}
              <tbody>
                {revAccounts.map(a => (
                  <tr key={a.code}>
                    <td className="font-mono text-zinc-400 w-16 pl-4">{a.code}</td>
                    <td>{a.name}</td>
                    <td className="text-right font-mono pr-4 text-emerald-600">{formatCurrency(a.balance)}</td>
                    {hasPY && <td className="text-right font-mono pr-4 text-zinc-400">{formatCurrency(a.py_balance || 0)}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-200">
                  <td colSpan="2" className="font-semibold pl-4">Total</td>
                  <td className="text-right font-mono font-semibold pr-4 text-emerald-600">{formatCurrency(revTotal)}</td>
                  {hasPY && <td className="text-right font-mono font-semibold pr-4 text-zinc-400">{formatCurrency(py.revenue_total)}</td>}
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm uppercase tracking-wider text-zinc-500">Expenses</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="report-table" data-testid="pl-expense-table">
              {hasPY && <thead><tr><th className="pl-4">Code</th><th>Account</th><th className="text-right">Current</th><th className="text-right pr-4 text-zinc-400">PY</th></tr></thead>}
              <tbody>
                {expAccounts.map(a => (
                  <tr key={a.code}>
                    <td className="font-mono text-zinc-400 w-16 pl-4">{a.code}</td>
                    <td>{a.name}</td>
                    <td className="text-right font-mono pr-4 text-red-500">{formatCurrency(a.balance)}</td>
                    {hasPY && <td className="text-right font-mono pr-4 text-zinc-400">{formatCurrency(a.py_balance || 0)}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-200">
                  <td colSpan="2" className="font-semibold pl-4">Total</td>
                  <td className="text-right font-mono font-semibold pr-4 text-red-500">{formatCurrency(expTotal)}</td>
                  {hasPY && <td className="text-right font-mono font-semibold pr-4 text-zinc-400">{formatCurrency(py.expenses_total)}</td>}
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      </div>

      <div className={`p-5 rounded-xl ${net >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex justify-between items-center">
          <span className="font-semibold text-zinc-900">Net Income</span>
          <div className="text-right">
            <span className={`text-3xl font-mono font-bold ${net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(net)}</span>
            {hasPY && <p className="text-sm font-mono text-zinc-400 mt-1">PY: {formatCurrency(py.net_income)}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
